import {
  PayloadAction,
  WithSlice,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { Pagination } from "@sscs/types";
import { removeUnsavedChange } from "Modules/notification";
import { rootReducer } from "Store/rootReducer";
import { RootState } from "Store/store";
import {
  InsightVersionsSort,
  InsightsFilters,
  InsightsSearchSort,
  MyInsightsSearch,
} from "../types";

const initPagination: Pagination = {
  itemsPerPage: 10,
  currentPage: 1,
};

const initSort: InsightsSearchSort = {
  property: "affected_packages",
  reverse: true,
};

const initVersionsSort: InsightVersionsSort = {
  property: "purl",
  reverse: false,
};

type InsightsResultsState = {
  deletedFilters?: Array<keyof InsightsFilters>;
  filters?: InsightsFilters | null;
  pagination?: Pagination;
  sort?: InsightsSearchSort;
  expandedRows?: Record<
    string,
    { pagination: Pagination; sortBy: InsightVersionsSort }
  >;
  focusedRow?: null | string;
};

// Key is groupId
type InsightsState = Record<string, InsightsResultsState>;

const initialState: InsightsState = {};

export const clearGroupFilters = createAsyncThunk(
  "insights/clearGroupFilters",
  async (groupId: string | undefined, { dispatch }) => {
    if (groupId) {
      dispatch(removeUnsavedChange({ page: "insights", id: groupId }));
      dispatch(insightsSlice.actions.deleteInsightsResultsState(groupId));
    }
  },
);

export const insightsSlice = createSlice({
  name: "insights",
  initialState,
  reducers: {
    filterUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        savedSearchFilters: InsightsFilters;
        filters: InsightsFilters | null;
      }>,
    ) => {
      const { filters, groupId, savedSearchFilters } = action.payload;

      const newFilterKeys = Object.keys(filters ?? {}) as Array<
        keyof InsightsFilters
      >;
      const savedFilterKeys = Object.keys(savedSearchFilters) as Array<
        keyof InsightsFilters
      >;

      if (!state[groupId]) {
        state[groupId] = {};
      }

      state[groupId].filters = {};
      state[groupId].deletedFilters = [];
      state[groupId].expandedRows = {};
      state[groupId].focusedRow = null;

      savedFilterKeys.forEach((filter) => {
        if (!newFilterKeys.includes(filter)) {
          state[groupId].deletedFilters?.push(filter);
        }
      });

      state[groupId].filters = filters;

      const currentGroupState = state[groupId];
      if (!currentGroupState.pagination) {
        currentGroupState.pagination = initPagination;
      } else {
        currentGroupState.pagination.currentPage = 1;
      }
    },
    deleteInsightsResultsState: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (action.payload && state[action.payload]) {
        delete state[action.payload];
      }
    },
    paginationUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        pagination: Pagination;
      }>,
    ) => {
      const { groupId, pagination } = action.payload;

      if (!state[groupId]) {
        state[groupId] = {
          pagination,
        };
      } else {
        state[groupId].pagination = pagination;
        state[groupId].expandedRows = {};
        state[groupId].focusedRow = null;
      }
    },
    sortUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        sort: InsightsSearchSort;
      }>,
    ) => {
      const { groupId, sort } = action.payload;

      if (!state[groupId]) {
        state[groupId] = {
          sort,
        };
      } else {
        state[groupId].sort = sort;
        state[groupId].expandedRows = {};
        state[groupId].focusedRow = null;
      }

      const currentGroupState = state[groupId];
      if (!currentGroupState.pagination) {
        currentGroupState.pagination = initPagination;
      } else {
        currentGroupState.pagination.currentPage = 1;
      }
    },
    focusedRowUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        issueId: string;
      }>,
    ) => {
      state[action.payload.groupId].focusedRow = action.payload.issueId;
    },
    expandedRowsUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        issueId: string;
        expand: boolean;
      }>,
    ) => {
      if (!state[action.payload.groupId]) {
        state[action.payload.groupId] = { expandedRows: {} };
      }

      if (!state[action.payload.groupId]?.expandedRows) {
        state[action.payload.groupId].expandedRows = {};
      }

      const expandedRows = state[action.payload.groupId].expandedRows ?? {};

      if (action.payload.expand) {
        if (!expandedRows[action.payload.issueId]) {
          expandedRows[action.payload.issueId] = {
            pagination: initPagination,
            sortBy: initVersionsSort,
          };
        }
      } else {
        delete expandedRows[action.payload.issueId];
      }
    },
    expandedRowPaginationUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        issueId: string;
        pagination: Pagination;
      }>,
    ) => {
      if (!state[action.payload.groupId]) {
        state[action.payload.groupId] = { expandedRows: {} };
      }

      if (!state[action.payload.groupId]?.expandedRows) {
        state[action.payload.groupId].expandedRows = {
          [action.payload.issueId]: {
            pagination: action.payload.pagination,
            sortBy: initVersionsSort,
          },
        };
      }

      const expandedRows = state[action.payload.groupId].expandedRows ?? {};

      if (expandedRows[action.payload.issueId]) {
        expandedRows[action.payload.issueId].pagination =
          action.payload.pagination;
      }
    },
    expandedRowSortUpdated: (
      state,
      action: PayloadAction<{
        groupId: string;
        issueId: string;
        sort: InsightVersionsSort;
      }>,
    ) => {
      if (!state[action.payload.groupId]) {
        state[action.payload.groupId] = { expandedRows: {} };
      }

      if (!state[action.payload.groupId]?.expandedRows) {
        state[action.payload.groupId].expandedRows = {};
      }

      const expandedRows = state[action.payload.groupId].expandedRows ?? {};

      if (expandedRows[action.payload.issueId]) {
        expandedRows[action.payload.issueId].sortBy = action.payload.sort;
        expandedRows[action.payload.issueId].pagination.currentPage = 1;
      }
    },
  },
  selectors: {
    selectFilters: (state, groupId: string | undefined) =>
      groupId ? state[groupId]?.filters : undefined,
    selectDeletedKeys: (state, groupId: string | undefined) =>
      groupId ? state[groupId]?.deletedFilters : undefined,
    selectPagination: (state, groupId: string | undefined): Pagination =>
      groupId ? (state[groupId]?.pagination ?? initPagination) : initPagination,
    selectSort: (state, groupId: string | undefined): InsightsSearchSort =>
      groupId && state[groupId] ? (state[groupId]?.sort ?? initSort) : initSort,
    selectIsRowExpanded: (
      state,
      groupId: string | undefined,
      issueId: string,
    ): boolean =>
      groupId ? state[groupId]?.expandedRows?.[issueId] !== undefined : false,
    selectExpandedRowPagination: (
      state,
      groupId: string | undefined,
      issueId: string,
    ): Pagination =>
      groupId
        ? (state[groupId]?.expandedRows?.[issueId]?.pagination ??
          initPagination)
        : initPagination,
    selectExpandedRowSort: (
      state,
      groupId: string | undefined,
      issueId: string,
    ): InsightVersionsSort =>
      groupId
        ? (state[groupId]?.expandedRows?.[issueId]?.sortBy ?? initVersionsSort)
        : initVersionsSort,
    selectFocusedRow: (state, groupId: string | undefined): string | null =>
      groupId ? (state[groupId]?.focusedRow ?? null) : null,
  },
});

const injectedSlice = insightsSlice.injectInto(rootReducer);

export const {
  filterUpdated,
  paginationUpdated,
  sortUpdated,
  expandedRowsUpdated,
  expandedRowPaginationUpdated,
  expandedRowSortUpdated,
  focusedRowUpdated,
} = insightsSlice.actions;

export const {
  selectFilters,
  selectDeletedKeys,
  selectPagination,
  selectSort,
  selectExpandedRowSort,
  selectExpandedRowPagination,
  selectFocusedRow,
  selectIsRowExpanded,
} = injectedSlice.selectors;

const selectSavedSearchFilters = (
  _state: RootState,
  _groupId: string | undefined,
  data: MyInsightsSearch | undefined,
) => {
  return data?.filters;
};

export const selectInsightsFilters = createSelector(
  [selectFilters, selectDeletedKeys, selectSavedSearchFilters],
  (filters, deleted, savedSearchFilters) => {
    const mergedFilters = {
      ...(savedSearchFilters ?? {}),
      ...(filters ?? {}),
    };

    deleted?.forEach((filter) => {
      delete mergedFilters[filter];
    });

    return mergedFilters;
  },
);

export const selectIsInsightsSearchDirty = createSelector(
  [selectInsightsFilters, selectSavedSearchFilters],
  (filters, savedSearchFilters) => {
    return Object.keys(filters).length > 0
      ? !isEqual(savedSearchFilters ?? {}, filters ?? {})
      : false;
  },
);

declare module "Store/rootReducer" {
  export interface LazyLoadedSlices extends WithSlice<typeof insightsSlice> {}
}
